/**
 * 队列逐块重放工具：用于 AI 报告「预取后展开模拟流式」。
 * 把缓存的文本片段队列按固定间隔逐块累积渲染，模拟实时流式效果。
 * 支持增量追加：请求进行中先播放已收到的片段，后续新片段持续追加消费。
 */

export interface ChunkReplayer {
  start: () => void;
  stop: () => void;
  isDone: () => boolean;
  /** 标记流已结束（请求 done 后调用），播完剩余片段后真正完成 */
  complete: () => void;
}

export function createChunkReplayer(
  chunks: string[],
  onChunk: (display: string) => void,
  intervalMs = 40,
  onDone?: () => void,
): ChunkReplayer {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let index = 0;
  let display = '';
  let done = false;
  let finished = false;

  const markDone = () => {
    if (!done) {
      done = true;
      onDone?.();
    }
    timer = null;
  };

  const stop = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const tick = () => {
    if (index < chunks.length) {
      display += chunks[index];
      index++;
      onChunk(display);
    }
    // 流已结束且已播完队列 → 真正完成并停止；否则持续轮询等待新片段追加
    if (finished && index >= chunks.length) {
      markDone();
      return;
    }
    timer = setTimeout(tick, intervalMs);
  };

  const start = () => {
    stop();
    index = 0;
    display = '';
    done = false;
    finished = false;
    tick();
  };

  const complete = () => {
    finished = true;
    stop();
    tick();
  };

  return { start, stop, isDone: () => done, complete };
}
